import InputField from 'components/fields/InputField';
import SearchSelector from 'components/fields/SearchSelector';
import {
    archiveCustomer,
    getCustomer,
    getCustomerAcquisitionYears,
    updateCustomer,
} from 'interfaces/customer';
import { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import {
    isFormErrored,
    isValidEmail,
    isValidPhoneNumber,
} from 'utils/verification';
import SaveStatus from 'components/actions/SaveStatus';
import env from 'variables/config';
import fetchClient from 'interfaces/fetchClient';
import InputTagField from 'components/fields/InputTagField';
import { updateMyInfo, getCompanyAccounts } from 'interfaces/user';
import DeleteModal from 'components/actions/Delete';
import { useDisclosure } from '@chakra-ui/hooks';
import Card from 'components/card';
import {
    MdArrowBack,
    MdOutlineArchive,
    MdPersonOutline,
    MdOutlineReceiptLong,
    MdAccountBalance,
} from 'react-icons/md';

const SectionHeader = (props: { icon: any; title: string }) => {
    const { icon: Icon, title } = props;
    return (
        <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#01499b]/10 text-[#01499b] dark:bg-white/10 dark:text-white">
                <Icon className="h-4 w-4" />
            </div>
            <p className="text-sm font-semibold text-navy-700 dark:text-white">
                {title}
            </p>
        </div>
    );
};

const GroupLabel = (props: { children: any; first?: boolean }) => {
    const { children, first = false } = props;
    return (
        <p
            className={`col-span-12 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${
                first
                    ? '-mb-1'
                    : '-mb-1 mt-2 border-t border-gray-100 pt-4 dark:border-white/10'
            }`}
        >
            {children}
        </p>
    );
};

const EditInformationPU = (props: { id?: string; onPageChange: any }) => {
    const { id, onPageChange } = props;
    const navigate = useNavigate();
    const params = useParams();
    const [customer, setCustomer] = useState<any>(null);
    const [saveStatus, setSaveStatus] = useState<any>('none');
    const [user, setUser] = useState(null);
    const [acquisitionYears, setAcquisitionYears] = useState<string[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const { isOpen, onClose, onOpen } = useDisclosure();

    const initAccounts = async () => {
        const res = await getCompanyAccounts();
        if (res?.data) setAccounts(res.data);
    };

    const initUser = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        fetchClient
            .get(`${env.apiUrl}/user/me`, config)
            .then((res: any) => {
                const user = res.data;
                setUser(user);
            })
            .catch((err: any) => {
                console.log(err);
            });
    };

    const addNewTag = async (newTag: string) => {
        if (!user) return;
        await updateMyInfo({ tags: [...user.tags, newTag] });
    };

    const addMemberType = async (newType: string) => {
        if (!user) return;
        const updated = [...(user.memberTypes || []), newType];
        await updateMyInfo({ memberTypes: updated });
        setUser((prev: any) => ({ ...prev, memberTypes: updated }));
    };

    const handleChange = (e: any) => {
        setSaveStatus('saving');
        setCustomer((prevCustomer: any) => ({
            ...prevCustomer,
            [e.target.id]: e.target.value,
        }));
    };

    const initCustomer = async (id: string) => {
        const res = await getCustomer(id);
        if (res) {
            setCustomer(res);
            return;
        }
        navigate('/customers');
    };

    const editCustomer = async () => {
        if (!customer) return;

        const validationRules: any = {
            mail: (value: any) => !value || isValidEmail(value),
            phone: (value: any) => !value || isValidPhoneNumber(value),
            //vatNumber: (value: any) => !value || isValidVATNumber(value),
            //siren: (value: any) => !value || isValidSiren(value),
            company: (value: any) => !!value.trim(),
        };

        if (isFormErrored(customer, validationRules)) {
            setSaveStatus('error');
            return;
        }

        const data = {
            _id: customer._id,
            deliveryTourId: customer.deliveryTourId,
            deliveryAddress: customer.deliveryAddress,
            billingAddress: customer.billingAddress,
            billingEmail: customer.billingEmail,
            billingPeriod: customer.billingPeriod,
            contact: customer.contact,
            contactPoste: customer.contactPoste,
            website: customer.website,
            company: customer.company,
            customerId: customer.customerId,
            mail: customer.mail,
            notes: customer.notes,
            vatNumber: customer.vatNumber,
            siren: customer.siren,
            bankName: customer.bankName,
            bankCode: customer.bankCode,
            bankBranchCode: customer.bankBranchCode,
            bankAccountNumber: customer.bankAccountNumber,
            bankRibKey: customer.bankRibKey,
            bankBic: customer.bankBic,
            phone: customer.phone,
            phone2: customer.phone2,
            paymentMethod: customer.paymentMethod,
            displayName: customer.displayName,
            tags: customer.tags,
            anneeAcquisition: customer.anneeAcquisition,
            typeMembre: customer.typeMembre,
            status: customer.status,
            potentialMonthlyRevenue: customer.potentialMonthlyRevenue,
            assignedTo: customer.assignedTo,
        };
        const res = await updateCustomer(data);
        if (res.data) setSaveStatus('saved');
        else setSaveStatus('error');
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            editCustomer();
        }, 2000);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            navigate('/login');
        }
        initCustomer(params.id || id);
        initUser();
        initAccounts();
        (async () => {
            const res = await getCustomerAcquisitionYears();
            if (res) setAcquisitionYears(res);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <DeleteModal
                isOpen={isOpen}
                onClose={onClose}
                description="Êtes-vous sûr de vouloir archiver ce client ?"
                context="un client"
                archived={true}
                deleteFunction={() => {
                    archiveCustomer(params.id);
                    navigate('/customers');
                }}
            />
            <div>
                {/* Toolbar */}
                <div className="flex items-center justify-between">
                    <button
                        className="group -ml-2 flex items-center gap-1.5 rounded-xl px-2 py-1.5 text-xs font-medium text-gray-500 transition duration-200 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                        onClick={() => onPageChange('page')}
                    >
                        <MdArrowBack className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                        Retour
                    </button>
                    <div className="flex items-center gap-2">
                        <SaveStatus status={saveStatus} />
                        <button
                            className="linear flex cursor-pointer items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 transition duration-200 hover:border-red-300 hover:bg-red-50 hover:text-red-500 dark:border-white/10 dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                            onClick={() => onOpen()}
                        >
                            <MdOutlineArchive className="h-4 w-4" />
                            Archiver
                        </button>
                    </div>
                </div>

                {/* Identity */}
                <div className="mt-4 flex items-center gap-3 px-1">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#01499b]/10 text-lg font-bold text-[#01499b] dark:bg-white/10 dark:text-white">
                        {customer?.company
                            ? customer.company.charAt(0).toUpperCase()
                            : '?'}
                    </div>
                    <div className="min-w-0">
                        <h4 className="truncate text-lg font-bold text-navy-700 dark:text-white">
                            {customer ? customer.company : 'Édition client'}
                        </h4>
                        {customer?.displayName &&
                            customer.displayName !== customer.company && (
                                <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                                    {customer.displayName}
                                </p>
                            )}
                    </div>
                    {customer?.customerId && (
                        <span className="ml-auto shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-white/10 dark:text-gray-300">
                            N° {customer.customerId}
                        </span>
                    )}
                </div>

                {/* Sections */}
                <div className="mt-5 flex flex-col gap-5">
                    <Card extra="w-full !p-5">
                        <SectionHeader
                            icon={MdPersonOutline}
                            title="Informations client"
                        />
                        <div className="mt-4 grid grid-cols-12 gap-4">
                            <GroupLabel first>Identité</GroupLabel>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Numéro client (compta)"
                                    placeholder="00012"
                                    value={customer?.customerId}
                                    id="customerId"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-5">
                                <InputField
                                    label="Nom commercial"
                                    placeholder="Nom commercial"
                                    value={
                                        customer?.displayName
                                            ? customer?.displayName
                                            : ''
                                    }
                                    id="displayName"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="Raison sociale*"
                                    placeholder="Toola"
                                    value={customer?.company}
                                    id="company"
                                    type="text"
                                    onChange={handleChange}
                                    error="Raison sociale invalide"
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>

                            <GroupLabel>Contact</GroupLabel>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="Email"
                                    placeholder="mathieu@toola.eu"
                                    error={'Invalide'}
                                    isInvalid={!isValidEmail(customer?.mail)}
                                    value={
                                        customer?.mail ? customer?.mail : ''
                                    }
                                    id="mail"
                                    type="email"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label="Téléphone"
                                    placeholder="Téléphone"
                                    error={'invalide'}
                                    isInvalid={
                                        !isValidPhoneNumber(customer?.phone)
                                    }
                                    value={customer?.phone}
                                    id="phone"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label="Téléphone 2"
                                    placeholder="Téléphone 2"
                                    error={'invalide'}
                                    isInvalid={
                                        customer?.phone2 &&
                                        !isValidPhoneNumber(customer?.phone2)
                                    }
                                    value={customer?.phone2}
                                    id="phone2"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label={`Contact`}
                                    placeholder="Contact"
                                    value={customer?.contact}
                                    id="contact"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label={`Poste du contact`}
                                    placeholder="Directeur achats"
                                    value={customer?.contactPoste || ''}
                                    id="contactPoste"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label={`Site web`}
                                    placeholder="https://www.client.fr"
                                    value={customer?.website || ''}
                                    id="website"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>

                            <GroupLabel>Suivi commercial</GroupLabel>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label={`Montant mensuel envisageable`}
                                    placeholder="500"
                                    value={customer?.potentialMonthlyRevenue}
                                    id="potentialMonthlyRevenue"
                                    type="number"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2 z-50">
                                <SearchSelector
                                    label="Commercial assigné"
                                    displayKey="fullName"
                                    options={accounts}
                                    value={
                                        customer?.assignedTo?.length > 0
                                            ? customer.assignedTo[0]
                                            : null
                                    }
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            assignedTo: [e._id],
                                        }));
                                    }}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2 z-50">
                                <SearchSelector
                                    label="Année d'acquisition"
                                    displayKey="name"
                                    options={acquisitionYears.map(
                                        (y: string) => ({ _id: y, name: y }),
                                    )}
                                    value={customer?.anneeAcquisition}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            anneeAcquisition: e._id,
                                        }));
                                    }}
                                    onCreateOption={(newYear: string) =>
                                        setAcquisitionYears((prev) => [
                                            ...prev,
                                            newYear,
                                        ])
                                    }
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2 z-50">
                                <SearchSelector
                                    label="Type de membre"
                                    displayKey="name"
                                    options={(user?.memberTypes || []).map(
                                        (t: string) => ({ _id: t, name: t }),
                                    )}
                                    value={customer?.typeMembre}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            typeMembre: e._id,
                                        }));
                                    }}
                                    onCreateOption={addMemberType}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-6">
                                <InputField
                                    label={`Notes utiles`}
                                    placeholder="Notes utiles"
                                    value={customer?.notes}
                                    id="notes"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>

                            <GroupLabel>Tags</GroupLabel>
                            <div className="z-50 col-span-12">
                                <InputTagField
                                    label="Tags clients"
                                    placeholder=""
                                    id="tags"
                                    type="text"
                                    extra=""
                                    tags={customer?.tags}
                                    allTags={
                                        user
                                            ? user.tags.map(
                                                  (name: string) => ({
                                                      name,
                                                  }),
                                              )
                                            : []
                                    }
                                    onTagCreate={(tag: any) => addNewTag(tag)}
                                    onChange={(tags: any) =>
                                        handleChange({
                                            target: {
                                                id: 'tags',
                                                value: tags,
                                            },
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </Card>

                    <Card extra="w-full !p-5">
                        <SectionHeader
                            icon={MdOutlineReceiptLong}
                            title="Informations de facturation"
                        />
                        <div className="mt-4 grid grid-cols-12 gap-4">
                            <GroupLabel first>Facturation</GroupLabel>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Email de facturation"
                                    placeholder="mathieu@toola.eu"
                                    error={'Email invalide'}
                                    isInvalid={
                                        !isValidEmail(customer?.billingEmail)
                                    }
                                    value={
                                        customer?.billingEmail
                                            ? customer?.billingEmail
                                            : ''
                                    }
                                    id="billingEmail"
                                    type="email"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <SearchSelector
                                    label="Période de facturation"
                                    displayKey="name"
                                    value={customer?.billingPeriod}
                                    inputSmall={true}
                                    options={[
                                        {
                                            _id: 'flexible',
                                            name: 'flexible',
                                        },
                                        {
                                            _id: 'delivery',
                                            name: 'à la livraison',
                                        },
                                        {
                                            _id: '7-days',
                                            name: '7 jours',
                                        },
                                        {
                                            _id: '10-days',
                                            name: '10 jours',
                                        },
                                        {
                                            _id: '15-days',
                                            name: '15 jours',
                                        },
                                        { _id: '1-month', name: '1 mois' },
                                    ]}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            billingPeriod: e._id,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-6">
                                <InputField
                                    label={`Adresse de facturation`}
                                    placeholder="Champ de Mars, 5 Av. Anatole France, 75007 Paris, France"
                                    id="billingAddress"
                                    type="text"
                                    value={
                                        customer?.billingAddress
                                            ? customer?.billingAddress
                                            : ''
                                    }
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>

                            <GroupLabel>Paiement &amp; fiscalité</GroupLabel>
                            <div className="col-span-12 lg:col-span-4">
                                <SearchSelector
                                    label="Moyen de paiement*"
                                    displayKey="name"
                                    value={customer?.paymentMethod}
                                    inputSmall={true}
                                    options={[
                                        {
                                            _id: 'cash',
                                            name: 'Espèces',
                                        },
                                        {
                                            _id: 'check',
                                            name: 'Chèque',
                                        },
                                        {
                                            _id: 'transfer',
                                            name: 'Virement',
                                        },
                                        {
                                            _id: 'credit-card',
                                            name: 'Carte bancaire',
                                        },
                                        {
                                            _id: 'direct-debit',
                                            name: 'Prélèvement',
                                        },
                                        {
                                            _id: 'LCR',
                                            name: 'LCR',
                                        },
                                    ].filter(
                                        (e) =>
                                            !user ||
                                            user?.acceptedPaymentMethods?.includes(
                                                e._id,
                                            ),
                                    )}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            paymentMethod: e._id,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label={`Numéro de TVA`}
                                    placeholder="FR32123456789"
                                    value={customer?.vatNumber}
                                    id="vatNumber"
                                    type="text"
                                    onChange={handleChange}
                                    // isInvalid={
                                    //     !isValidVATNumber(customer?.vatNumber)
                                    // }
                                    error={'Numéro de TVA invalide'}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label={`Numéro de SIREN`}
                                    placeholder="123568941"
                                    value={customer?.siren}
                                    id="siren"
                                    type="text"
                                    onChange={handleChange}
                                    //isInvalid={!isValidSiren(customer?.siren)}
                                    error={'SIREN invalide'}
                                    inputSmall={true}
                                />
                            </div>
                        </div>
                    </Card>

                    <Card extra="w-full !p-5">
                        <SectionHeader
                            icon={MdAccountBalance}
                            title="Banque"
                        />
                        <div className="mt-4 grid grid-cols-12 gap-4">
                            <GroupLabel first>Coordonnées bancaires</GroupLabel>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="Nom de la banque"
                                    placeholder="BNP Paribas"
                                    value={customer?.bankName || ''}
                                    id="bankName"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-2">
                                <InputField
                                    label="Code banque"
                                    placeholder="30004"
                                    value={customer?.bankCode || ''}
                                    id="bankCode"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-2">
                                <InputField
                                    label="Code guichet"
                                    placeholder="00825"
                                    value={customer?.bankBranchCode || ''}
                                    id="bankBranchCode"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="N° de compte"
                                    placeholder="00001234567"
                                    value={customer?.bankAccountNumber || ''}
                                    id="bankAccountNumber"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-2">
                                <InputField
                                    label="Clé RIB"
                                    placeholder="89"
                                    value={customer?.bankRibKey || ''}
                                    id="bankRibKey"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-4">
                                <InputField
                                    label="BIC"
                                    placeholder="BNPAFRPPXXX"
                                    value={customer?.bankBic || ''}
                                    id="bankBic"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default EditInformationPU;
